/**
 * SEO and JSON-LD for the event categories hub (`/event`).
 * Aligns with `place/index.seo.ts`: absolute URLs, robots by environment, and a JSON-LD array
 * (`CollectionPage` + `BreadcrumbList`) for richer indexing.
 */

import allEventsImage from '@web/src/assets/event/event-all-events.webp?w=1200&format=webp'
import campingImage from '@web/src/assets/event/event-camping.webp?w=1200&format=webp'
import clubEventsImage from '@web/src/assets/event/event-club-events.webp?w=1200&format=webp'
import cruisesImage from '@web/src/assets/event/event-cruises.webp?w=1200&format=webp'
import housePartiesImage from '@web/src/assets/event/event-house-parties.webp?w=1200&format=webp'
import kinkImage from '@web/src/assets/event/event-kink.webp?w=1200&format=webp'
import noClubEventsImage from '@web/src/assets/event/event-no-club-events.webp?w=1200&format=webp'
import otherImage from '@web/src/assets/event/event-other.webp?w=1200&format=webp'
import socialAndMeetImage from '@web/src/assets/event/event-social-and-meet.webp?w=1200&format=webp'
import takeoversImage from '@web/src/assets/event/event-takeovers.webp?w=1200&format=webp'
import vacationImage from '@web/src/assets/event/event-vacation.webp?w=1200&format=webp'
import { env } from '@web/src/lib/env.client'
import i18n from '@web/src/lib/intl/i18n'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { SEO_META_DESCRIPTION_MAX_CHARS, truncateMetaDescription } from '@web/src/lib/seo/seo-common'
import { absoluteUrlForPath } from '@web/src/lib/utilsClient'
import type { EventCategoryEnum } from 'types/graphql'

export type EventCategoryCountsForSeo = {
  allEvents: number
  camping: number
  clubs: number
  kink: number
  houseParties: number
  socialAndMeet: number
  takeover: number
  cruises: number
  vacation: number
  other: number
  noClubEvents: number
}

export type EventCategoriesIndexSeoLoaderInput = {
  eventCategoryCounts?: EventCategoryCountsForSeo | null
}

/** Same exclusions as `index.tsx` “Non-Club Events” card — comma list matches `/event/list` `categories` search. */
const EVENT_CATEGORIES_EXCEPT_CLUBS: EventCategoryEnum[] = [
  'KINK',
  'CAMPING',
  'HOUSE_PARTIES',
  'SOCIAL_AND_MEET',
  'TAKEOVER',
  'CRUISES',
  'VACATION',
  'OTHER',
]

type EventTileSeoConfig = {
  titleKey: string
  imagePath: string
  countKey: keyof EventCategoryCountsForSeo
  /** Comma-separated `categories` query param (matches event index navigation). */
  categoriesCsv?: string
  category?: EventCategoryEnum
  distance?: number
}

/** Same tiles as `index.tsx` cards — used only for structured data URLs and labels. */
const CATEGORY_TILES_FOR_JSON_LD: EventTileSeoConfig[] = [
  { titleKey: 'All Events', imagePath: allEventsImage, countKey: 'allEvents' },
  {
    titleKey: 'Non-Club Events',
    imagePath: noClubEventsImage,
    countKey: 'noClubEvents',
    categoriesCsv: EVENT_CATEGORIES_EXCEPT_CLUBS.join(','),
  },
  { titleKey: 'Camping', imagePath: campingImage, countKey: 'camping', category: 'CAMPING' },
  { titleKey: 'Club Events', imagePath: clubEventsImage, countKey: 'clubs', category: 'CLUBS' },
  { titleKey: 'Cruises', imagePath: cruisesImage, countKey: 'cruises', category: 'CRUISES' },
  {
    titleKey: 'House Parties',
    imagePath: housePartiesImage,
    countKey: 'houseParties',
    category: 'HOUSE_PARTIES',
  },
  { titleKey: 'Kink', imagePath: kinkImage, countKey: 'kink', category: 'KINK' },
  { titleKey: 'Other', imagePath: otherImage, countKey: 'other', category: 'OTHER' },
  {
    titleKey: 'Social & Meetups',
    imagePath: socialAndMeetImage,
    countKey: 'socialAndMeet',
    category: 'SOCIAL_AND_MEET',
  },
  { titleKey: 'Takeovers', imagePath: takeoversImage, countKey: 'takeover', category: 'TAKEOVER' },
  {
    titleKey: 'Vacations',
    imagePath: vacationImage,
    countKey: 'vacation',
    category: 'VACATION',
  },
]

function absoluteDiscoverEventListUrl(input: { categoriesCsv?: string; distance?: number }): string {
  const base = absoluteUrlForPath('/event/list')
  const u = new URL(base)
  u.searchParams.set('page', '1')
  if (input.categoriesCsv) {
    u.searchParams.set('categories', input.categoriesCsv)
  }
  if (input.distance != null) {
    u.searchParams.set('distance', String(input.distance))
  }
  return u.href
}

function listUrlForTile(tile: EventTileSeoConfig): string {
  const categoriesCsv = tile.categoriesCsv ?? tile.category
  return absoluteDiscoverEventListUrl({
    categoriesCsv: categoriesCsv || undefined,
    distance: tile.distance,
  })
}

function countForTile(tile: EventTileSeoConfig, counts: EventCategoryCountsForSeo | null | undefined): number {
  if (!counts) return 0
  return counts[tile.countKey] ?? 0
}

function buildEventCategoriesBreadcrumbJsonLd(pageUrl: string, tr: typeof i18n.t): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: tr('Home'), item: absoluteUrlForPath('/') },
      {
        '@type': 'ListItem',
        position: 2,
        name: tr('Event Categories'),
        item: pageUrl,
      },
    ],
  }
}

function buildEventCategoriesCollectionJsonLd(input: {
  tr: typeof i18n.t
  pageUrl: string
  pageTitle: string
  pageDescription: string
  counts: EventCategoryCountsForSeo | null | undefined
}): Record<string, unknown> {
  const { tr, pageUrl, pageTitle, pageDescription, counts } = input

  const itemListElement = CATEGORY_TILES_FOR_JSON_LD.map((tile, index) => {
    const title = tr(tile.titleKey)
    const listUrl = listUrlForTile(tile)
    const imageUrl = absoluteUrlForPath(tile.imagePath)
    const n = countForTile(tile, counts)
    const description = n > 0 ? tr('{{count}} Events', { count: n }) : undefined

    return {
      '@type': 'ListItem',
      position: index + 1,
      name: title,
      item: {
        '@type': 'WebPage',
        name: title,
        url: listUrl,
        image: imageUrl,
        ...(description ? { description } : {}),
      },
    }
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDescription,
    url: pageUrl,
    image: absoluteUrlForPath('/logo-og-image.webp'),
    mainEntity: {
      '@type': 'ItemList',
      name: tr('Event categories'),
      numberOfItems: CATEGORY_TILES_FOR_JSON_LD.length,
      itemListElement,
    },
  }
}

function buildMetaDescription(tr: typeof i18n.t, counts: EventCategoryCountsForSeo | null | undefined): string {
  const base = tr(
    'Discover lifestyle and swinger event categories on SpicyFriends: clubs, house parties, camping, meetups, and filtered lists for your vibe and plans.'
  )
  if (counts != null && counts.allEvents > 0) {
    const withCount = tr(
      '{{count}} lifestyle and swinger events on SpicyFriends: browse clubs, house parties, camping, meetups, and more with filters for vibe and distance.',
      {
        count: counts.allEvents,
      }
    )
    if (withCount.length <= SEO_META_DESCRIPTION_MAX_CHARS) return withCount
    return truncateMetaDescription(withCount, SEO_META_DESCRIPTION_MAX_CHARS)
  }
  if (base.length <= SEO_META_DESCRIPTION_MAX_CHARS) return base
  return truncateMetaDescription(base, SEO_META_DESCRIPTION_MAX_CHARS)
}

export function eventCategoriesIndexHeadFromLoaderData(
  loaderData: EventCategoriesIndexSeoLoaderInput | null | undefined
) {
  const tr = i18n.t.bind(i18n)
  const counts = loaderData?.eventCategoryCounts ?? undefined
  const pageUrl = absoluteUrlForPath('/event')
  const pageTitle = tr('Find Your Next Event')
  const pageDescription = buildMetaDescription(tr, counts)

  const collectionPage = buildEventCategoriesCollectionJsonLd({
    tr,
    pageUrl,
    pageTitle,
    pageDescription,
    counts,
  })
  const breadcrumbList = buildEventCategoriesBreadcrumbJsonLd(pageUrl, tr)

  return seo({
    title: `${pageTitle} | ${env.VITE_APP_NAME}`,
    description: pageDescription,
    url: pageUrl,
    image: '/logo-og-image.webp',
    robots: robotsMetaForEnvironment({
      production: 'index, follow',
      staging: 'noindex,nofollow',
    }),
    jsonLd: [collectionPage, breadcrumbList],
  })
}
